import { useEffect, useState } from 'react'
import { api } from '../api/client'
import type { Ref } from '../components/common/Refs'

/** Ein Eintrag im Block „Verknüpft" des Meldungsdetails. */
export interface Verknuepfung {
  typ: 'Setup' | 'Einschätzung' | 'Fall' | 'Idee'
  /** Die Farbe trägt den Zustand: gut, warnend, schlecht, laufend, offen. */
  ton: 'good' | 'warn' | 'bad' | 'accent' | 'dim'
  status: string
  text: string
  /** Wohin der Klick führt. */
  to: string
}

/** Wie viele Ziele einer Meldung nachgeschlagen werden — mehr wären Rauschen. */
const MAX_ZIELE = 3

//: Der Daily Stretch wird EINMAL geholt und für alle Meldungen behalten: Er
//: ist ein Tagesbericht über die ganze Watchlist, kein Nachschlagewerk je
//: Titel. Beim Seitenwechsel verfällt er mit dem Modul nicht — die Seite lädt
//: ihn beim nächsten Besuch ohnehin neu, weil sich der Handelstag ändert.
let stretch: Promise<Map<string, { dip: number | null; status: string }>> | null = null

function stretchNachIsin() {
  stretch ??= api.dailyStretch().then((bericht) => {
    const nachIsin = new Map<string, { dip: number | null; status: string }>()
    for (const zeile of bericht.rows) {
      if (zeile.isin) nachIsin.set(zeile.isin, { dip: zeile.dip_score, status: zeile.status })
    }
    return nachIsin
  }).catch(() => { stretch = null; return new Map() })
  return stretch
}

function ampelTon(rating: string): Verknuepfung['ton'] {
  return rating === 'GREEN' ? 'good' : rating === 'RED' ? 'bad' : 'warn'
}

function wirkungTon(wirkung: string): Verknuepfung['ton'] {
  if (wirkung === 'POSITIVE') return 'good'
  if (wirkung === 'NEGATIVE') return 'bad'
  if (wirkung === 'CAUTION') return 'warn'
  return 'dim'
}

/** Was der Haken braucht: eine Kennung und die Ziele — Meldung oder Ereignis. */
export interface MitZielen {
  id: number
  targets: Ref[]
}

/**
 * Was im System zu den Zielen einer Meldung oder eines Ereignisses schon
 * steht: die jüngste Einschätzung, laufende Fälle, offene Handelsideen, das
 * Setup des Tages.
 *
 * AUS DEN VORHANDENEN LISTEN, nicht aus einem eigenen Endpunkt: Je Ziel eine
 * Einschätzung, je Titel Fälle und Ideen. Höchstens drei Ziele, damit eine
 * Meldung über sechs Länder nicht zwanzig Abfragen auslöst. Die Antwort ist
 * ein Hinweis, kein Beleg — wer es genau wissen will, klickt hinüber.
 */
export function useVerknuepft(item: MitZielen | null): {
  eintraege: Verknuepfung[]
  laedt: boolean
} {
  const [eintraege, setEintraege] = useState<Verknuepfung[]>([])
  const [laedt, setLaedt] = useState(false)

  const id = item?.id ?? null
  const ziele = item?.targets.slice(0, MAX_ZIELE) ?? []
  const schluessel = ziele.map((z) => z.ref).join(',')

  useEffect(() => {
    if (id === null || ziele.length === 0) { setEintraege([]); return }
    const controller = new AbortController()
    const { signal } = controller
    setLaedt(true)

    // Ein Fehler in EINER Abfrage nimmt die anderen nicht mit.
    const weich = <T,>(p: Promise<T>) => p.catch(() => null)

    const isins = ziele.filter((z) => z.ref.startsWith('isin:'))
      .map((z) => z.ref.slice('isin:'.length))

    Promise.all([
      ...ziele.map((z) => weich(api.assessments(
        { ref: z.ref, sort: 'assessed_at', dir: 'desc', size: 1 }, signal))),
      ...isins.map((isin) => weich(api.researchCases(
        { isin, active_only: true, size: 3 }, signal))),
      ...isins.map((isin) => weich(api.tradeIdeas(
        { isin, open_only: true, size: 3 }, signal))),
      isins.length ? stretchNachIsin() : Promise.resolve(new Map()),
    ]).then((antworten) => {
      if (signal.aborted) return
      const gefunden: Verknuepfung[] = []
      const n = ziele.length, m = isins.length
      const einschaetzungen = antworten.slice(0, n)
      const faelle = antworten.slice(n, n + m)
      const ideen = antworten.slice(n + m, n + 2 * m)
      const setups = antworten[n + 2 * m] as Map<string, { dip: number | null; status: string }>

      isins.forEach((isin) => {
        const setup = setups.get(isin)
        if (setup) gefunden.push({
          typ: 'Setup', ton: setup.dip !== null && setup.dip >= 70 ? 'good' : 'dim',
          status: setup.status,
          text: `Dip ${setup.dip ?? '–'} · ${setup.status}`, to: '/reports/daily-stretch',
        })
      })
      ziele.forEach((ziel, i) => {
        const antwort = einschaetzungen[i] as { items: { rating: string; headline: string }[] } | null
        const jüngste = antwort?.items?.[0]
        if (jüngste) gefunden.push({
          typ: 'Einschätzung', ton: ampelTon(jüngste.rating), status: jüngste.rating,
          text: jüngste.headline, to: `/assessments?ref=${encodeURIComponent(ziel.ref)}`,
        })
      })
      faelle.forEach((antwort) => {
        const seite = antwort as { items: { id: number; title: string; trading_impact: string; overhang_score: number | null }[] } | null
        for (const fall of seite?.items ?? []) gefunden.push({
          typ: 'Fall', ton: wirkungTon(fall.trading_impact), status: fall.trading_impact,
          text: fall.overhang_score !== null ? `${fall.title} · Overhang ${fall.overhang_score}` : fall.title,
          to: `/research/${fall.id}`,
        })
      })
      ideen.forEach((antwort, i) => {
        const seite = antwort as { items: { headline: string; status: string }[] } | null
        for (const idee of seite?.items ?? []) gefunden.push({
          typ: 'Idee', ton: idee.status === 'PROPOSED' ? 'dim' : 'accent', status: idee.status,
          text: idee.headline, to: `/trade-ideas?isin=${isins[i]}`,
        })
      })
      setEintraege(gefunden)
    }).finally(() => { if (!signal.aborted) setLaedt(false) })

    return () => controller.abort()
    // `schluessel` fasst die Ziele zusammen; `ziele` selbst wäre bei jedem
    // Rendern ein neues Array.
  }, [id, schluessel]) // eslint-disable-line react-hooks/exhaustive-deps

  return { eintraege, laedt }
}
